import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { Construction, Copy, KeyRound, Lock, Plus, ShieldCheck, Trash2, Unlock, Wallet } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useDemo } from "@/store/DemoStore";
import { Avatar, Card, Empty, PageHeader, avatarUrl, btn } from "@/components/common";
import { cn } from "@/lib/utils";

const field = "h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-base text-navy-900 outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100 sm:text-sm";

export const AGENT_TABS = [
  ["commissions", "Commissions"],
  ["permissions", "Permissions"],
  ["operations", "Operations"],
  ["talent", "Talent"],
];

function Credentials({ creds, onClose }) {
  const text = `Username: ${creds.username}\nTemporary password: ${creds.tempPassword}\nSign in: ${window.location.origin}/portal`;
  return (
    <div className="space-y-4" data-testid="agent-credentials">
      <p className="text-sm text-slate-600">Share these with the agent. The temporary password is shown only once and must be changed at first sign in.</p>
      <pre className="whitespace-pre-wrap break-all rounded-xl bg-slate-50 p-4 font-mono text-sm text-navy-900">{text}</pre>
      <div className="flex justify-end gap-2">
        <button onClick={() => { navigator.clipboard?.writeText(text); toast.success("Copied"); }} className={btn.secondary} data-testid="agent-credentials-copy"><Copy className="h-4 w-4" />Copy</button>
        <button onClick={onClose} className={btn.primary} data-testid="agent-credentials-done">Done</button>
      </div>
    </div>
  );
}

function NewAgentDialog({ open, onOpenChange }) {
  const { createAgent } = useDemo();
  const [f, setF] = useState({ firstName: "", lastName: "", email: "", title: "Travel Advisor", phone: "" });
  const [creds, setCreds] = useState(null);
  const username = (f.firstName[0] || "") + f.lastName;
  const submit = async (e) => {
    e.preventDefault();
    const r = await createAgent(f);
    if (r) setCreds(r);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-lg rounded-2xl p-5 sm:p-7" data-testid="new-agent-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-navy-900">{creds ? "Agent created" : "Add Agent"}</DialogTitle>
          <DialogDescription>{creds ? `${creds.username} can now sign in.` : `Username will be ${username.toLowerCase().replace(/[^a-z]/g, "") || "first initial + last name"}.`}</DialogDescription>
        </DialogHeader>
        {creds ? <Credentials creds={creds} onClose={() => onOpenChange(false)} /> : (
          <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
            {[["firstName", "First name", true], ["lastName", "Last name", true], ["email", "Email", false], ["phone", "Phone", false], ["title", "Title", false]].map(([k, l, req]) => (
              <label key={k} className={k === "title" ? "sm:col-span-2" : ""}><span className="mb-1.5 block text-xs font-medium text-slate-500">{l}</span>
                <input required={req} type={k === "email" ? "email" : "text"} className={field} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`new-agent-${k}`} /></label>
            ))}
            <div className="flex justify-end gap-2 pt-2 sm:col-span-2">
              <button type="button" onClick={() => onOpenChange(false)} className={btn.secondary}>Cancel</button>
              <button className={btn.primary} data-testid="new-agent-submit">Create Agent</button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AgentRow({ a, clients, onReset, onDelete }) {
  return (
    <li className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:px-6" data-testid={`agent-row-${a.username}`}>
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Avatar name={a.name} src={avatarUrl(a.id, a.avatar)} />
        <div className="min-w-0">
          <p className="flex items-center gap-2 truncate text-sm font-semibold text-navy-900">{a.name}{a.role === "admin" && <span className="inline-flex items-center gap-1 rounded-md bg-navy-900 px-1.5 py-0.5 text-[10px] font-semibold text-white"><ShieldCheck className="h-3 w-3" />ADMIN</span>}{!a.active && <span className="rounded-md bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">LOCKED</span>}</p>
          <p className="truncate text-xs text-slate-500"><span className="font-mono">{a.username}</span> · {a.title}{a.email && ` · ${a.email}`} · {clients} clients</p>
        </div>
      </div>
      {a.role !== "admin" && (
        <div className="flex gap-2">
          <button onClick={() => onReset(a)} className={btn.secondary} data-testid={`agent-reset-${a.username}`}><KeyRound className="h-4 w-4" />Reset Password</button>
          <button onClick={() => onDelete(a)} className="inline-flex h-10 items-center gap-2 rounded-xl border border-red-200 bg-white px-3.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50" data-testid={`agent-delete-${a.username}`}><Trash2 className="h-4 w-4" />Delete</button>
        </div>
      )}
    </li>
  );
}

function CommissionRow({ a }) {
  const { patchAgent } = useDemo();
  const base = a.commissionSplit ?? 60;
  const [v, setV] = useState(String(base));
  const rate = Math.min(100, Math.max(0, Math.round(+v || 0)));
  const dirty = rate !== base;
  return (
    <li className="flex items-center gap-3 px-4 py-4 sm:px-6" data-testid={`commission-row-${a.username}`}>
      <Avatar name={a.name} src={avatarUrl(a.id, a.avatar)} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-navy-900">{a.name}</p>
        <p className="truncate text-xs text-slate-500"><span className="font-mono">{a.username}</span> · {a.title}</p>
      </div>
      <div className="flex items-center gap-2">
        <div className="relative">
          <input type="number" min="0" max="100" value={v} onChange={(e) => setV(e.target.value)} data-testid={`commission-rate-${a.username}`} className="h-10 w-24 rounded-lg border border-slate-200 bg-white pl-3 pr-7 text-base text-navy-900 outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100 sm:text-sm" />
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">%</span>
        </div>
        <button disabled={!dirty} onClick={() => patchAgent(a.id, { commissionSplit: rate })} data-testid={`commission-save-${a.username}`} className={`${btn.primary} h-10`}>Save</button>
      </div>
    </li>
  );
}

export function AgentsCommissions() {
  const { agents } = useDemo();
  const list = agents.filter((a) => a.role !== "admin");
  return (
    <div data-testid="agents-commissions">
      <p className="mb-4 text-sm text-slate-500">Set each advisor's commission share. Changes apply to their entire portal immediately.</p>
      <Card>
        {list.length ? <ul className="divide-y divide-slate-100" data-testid="commissions-list">{list.map((a) => <CommissionRow key={a.id} a={a} />)}</ul>
          : <Empty icon={Wallet} title="No advisors yet" text="Add an agent in Operations to set their commission." />}
      </Card>
    </div>
  );
}

function PermissionRow({ a }) {
  const { patchAgent } = useDemo();
  const locked = !a.active;
  return (
    <li className="flex items-center gap-3 px-4 py-4 sm:px-6" data-testid={`permission-row-${a.username}`}>
      <Avatar name={a.name} src={avatarUrl(a.id, a.avatar)} />
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2 truncate text-sm font-semibold text-navy-900">{a.name}{locked && <span className="rounded-md bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">LOCKED</span>}{a.agreementSignedOn && <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid={`agreement-badge-${a.username}`}><ShieldCheck className="h-3 w-3" />Agreement Signed {new Date(a.agreementSignedOn).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>}</p>
        <p className="truncate text-xs text-slate-500"><span className="font-mono">{a.username}</span> · {locked ? "Portal access blocked" : "Portal access active"}</p>
      </div>
      <button onClick={() => patchAgent(a.id, { active: locked })} data-testid={`permission-toggle-${a.username}`} className={`h-10 ${locked ? btn.primary : btn.secondary}`}>
        {locked ? <><Unlock className="h-4 w-4" />Unlock access</> : <><Lock className="h-4 w-4" />Lock access</>}
      </button>
    </li>
  );
}

export function AgentsPermissions() {
  const { agents } = useDemo();
  const list = agents.filter((a) => a.role !== "admin");
  return (
    <div data-testid="agents-permissions">
      <p className="mb-4 text-sm text-slate-500">Lock an advisor to immediately block their portal access (e.g. unpaid dues); unlock to restore it.</p>
      <Card>
        {list.length ? <ul className="divide-y divide-slate-100" data-testid="permissions-list">{list.map((a) => <PermissionRow key={a.id} a={a} />)}</ul>
          : <Empty icon={Lock} title="No advisors yet" text="Add an agent in Operations to manage access." />}
      </Card>
    </div>
  );
}

export function AgentsLayout() {
  return (
    <div className="max-w-5xl" data-testid="admin-agents-page">
      <PageHeader title="Agents" subtitle="Admin only · commissions, permissions, operations and talent." />
      <div className="mb-6 flex gap-1 overflow-x-auto rounded-xl bg-slate-100 p-1 no-scrollbar" data-testid="agents-tabs">
        {AGENT_TABS.map(([to, label]) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`agents-tab-${to}`}
            className={({ isActive }) => cn("whitespace-nowrap rounded-lg px-3.5 py-1.5 text-sm font-medium transition-colors", isActive ? "bg-white text-navy-900 shadow-sm" : "text-slate-500 hover:text-navy-900")}
          >
            {label}
          </NavLink>
        ))}
      </div>
      <Outlet />
    </div>
  );
}

export function AgentsOperations() {
  const { agents, clients, patchAgent, deleteAgent } = useDemo();
  const [open, setOpen] = useState(false);
  const [creds, setCreds] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const reset = async (a) => {
    const r = await patchAgent(a.id, { resetPassword: true });
    if (r) setCreds({ username: a.username, tempPassword: r.tempPassword });
  };
  const confirmDelete = async () => {
    setDeleting(true);
    const r = await deleteAgent(toDelete.id);
    setDeleting(false);
    if (r) setToDelete(null);
  };
  return (
    <div data-testid="agents-operations">
      <div className="mb-4 flex justify-end">
        <button onClick={() => setOpen(true)} className={btn.primary} data-testid="add-agent-btn"><Plus className="h-4 w-4" />Add Agent</button>
      </div>
      <Card><ul className="divide-y divide-slate-100" data-testid="agents-list">
        {agents.map((a) => <AgentRow key={a.id} a={a} clients={clients.filter((c) => c.agentId === a.id).length} onReset={reset} onDelete={setToDelete} />)}
      </ul></Card>
      {open && <NewAgentDialog open onOpenChange={setOpen} />}
      <Dialog open={!!creds} onOpenChange={() => setCreds(null)}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-lg rounded-2xl p-5 sm:p-7">
          <DialogHeader><DialogTitle className="font-display text-xl text-navy-900">Password Reset</DialogTitle><DialogDescription>New temporary password for {creds?.username}.</DialogDescription></DialogHeader>
          {creds && <Credentials creds={creds} onClose={() => setCreds(null)} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md rounded-2xl p-5 sm:p-7" data-testid="delete-agent-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-navy-900">Delete Agent?</DialogTitle>
            <DialogDescription>This permanently removes <span className="font-semibold text-navy-900">{toDelete?.name}</span> ({toDelete?.username}) and their portal access. Agents with clients or groups must be reassigned first. This can't be undone.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setToDelete(null)} className={btn.secondary} data-testid="delete-agent-cancel">Cancel</button>
            <button onClick={confirmDelete} disabled={deleting} className="inline-flex h-11 items-center gap-2 rounded-xl bg-red-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-60" data-testid="delete-agent-confirm"><Trash2 className="h-4 w-4" />{deleting ? "Deleting…" : "Delete Agent"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function AgentsTab({ title, testId }) {
  return (
    <Card data-testid={testId}>
      <Empty icon={Construction} title={`${title} — coming soon`} text="This section is staged and ready to build." />
    </Card>
  );
}
